import React, { useState, useEffect } from 'react';
import { repasseService } from '../../services/repasseService';
import { useNotifier } from '../ui/Notifier';

interface Props {
  repasse?: any;
  documentoId?: string;
  isOpen: boolean;
  isEditing?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const RepasseFormModal: React.FC<Props> = ({ 
  repasse, 
  documentoId, 
  isOpen, 
  isEditing = false, 
  onClose, 
  onSuccess 
}) => {
  const [cliente, setCliente] = useState('');
  const [numeroDocumento, setNumeroDocumento] = useState('');
  const [dataPagamento, setDataPagamento] = useState('');
  const [repassePago, setRepassePago] = useState('');
  const [valorComissao, setValorComissao] = useState('');
  const [percentualRepasse, setPercentualRepasse] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [documentoIdField, setDocumentoIdField] = useState(documentoId || '');
  const [submitting, setSubmitting] = useState(false);
  const notifier = useNotifier();
  
  // Dados do documento relacionado (readOnly)
  const [nomeCliente, setNomeCliente] = useState('');
  const [seguradora, setSeguradora] = useState('');
  const [produto, setProduto] = useState('');

  useEffect(() => {
    if (repasse && isEditing) {
      setCliente(repasse.cliente || '');
      setNumeroDocumento(repasse.numeroDocumento || '');
      setDataPagamento(repasse.dataPagamento ? new Date(repasse.dataPagamento).toISOString().split('T')[0] : '');
      setRepassePago(repasse.repassePago ? String(repasse.repassePago) : '');
      setValorComissao(repasse.valorComissao ? String(repasse.valorComissao) : '');
      setPercentualRepasse(repasse.percentualRepasse ? String(repasse.percentualRepasse) : '');
      setObservacoes(repasse.observacoes || '');
      setDocumentoIdField(repasse.documentoId || documentoId || '');
      
      // Dados do cliente/documento
      setNomeCliente(repasse.clienteData?.nome || repasse.documento?.cliente?.nome || '');
      setSeguradora(repasse.documento?.seguradora?.nome || '');
      setProduto(repasse.documento?.produto?.nome || '');
    } else {
      setCliente('');
      setNumeroDocumento('');
      setDataPagamento('');
      setRepassePago('');
      setValorComissao('');
      setPercentualRepasse('');
      setObservacoes('');
      setDocumentoIdField(documentoId || '');
      setNomeCliente('');
      setSeguradora('');
      setProduto('');
    }
  }, [repasse, isEditing, isOpen, documentoId]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: any = { 
        cliente, 
        numeroDocumento,
        dataPagamento: dataPagamento ? new Date(dataPagamento).toISOString() : undefined, 
        repassePago: repassePago ? parseFloat(repassePago) : undefined,
        valorComissao: valorComissao ? parseFloat(valorComissao) : undefined,
        percentualRepasse: percentualRepasse ? parseFloat(percentualRepasse) : undefined,
        observacoes: observacoes || undefined,
        documentoId: documentoIdField || undefined
      };
      
      if (isEditing && repasse?.id) {
        await repasseService.update(repasse.id, payload);
      } else {
        await repasseService.create(payload);
      }
      
      notifier.show({ message: isEditing ? 'Repasse atualizado com sucesso!' : 'Repasse criado com sucesso!', severity: 'success' });
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao salvar repasse', err);
      notifier.show({ message: 'Erro ao salvar repasse', severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded p-4 w-full max-w-md">
        <h3 className="text-lg font-medium mb-2">
          {isEditing ? 'Editar Repasse' : 'Novo Repasse'}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-2 text-xs">
          {/* Campos ReadOnly - Informações do Documento */}
          {(nomeCliente || seguradora || produto) && (
            <div className="bg-gray-50 p-3 rounded border border-gray-200 space-y-2">
              <h4 className="font-semibold text-gray-700">Informações do Documento</h4>
              {nomeCliente && (
                <div>
                  <label className="block text-gray-500 text-xs">Cliente</label>
                  <div className="text-sm font-medium text-gray-900">{nomeCliente}</div>
                </div>
              )}
              {seguradora && (
                <div>
                  <label className="block text-gray-500 text-xs">Seguradora</label>
                  <div className="text-sm font-medium text-gray-900">{seguradora}</div>
                </div>
              )}
              {produto && (
                <div>
                  <label className="block text-gray-500 text-xs">Produto</label>
                  <div className="text-sm font-medium text-gray-900">{produto}</div>
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-gray-600">Número da Proposta</label>
            <input 
              className="w-full p-2 border rounded" 
              value={numeroDocumento} 
              onChange={(e) => setNumeroDocumento(e.target.value)} 
              placeholder="Número da proposta"
            />
          </div>
          <div>
            <label className="block text-gray-600">Data Pagamento</label>
            <input 
              type="date" 
              className="w-full p-2 border rounded" 
              value={dataPagamento} 
              onChange={(e) => setDataPagamento(e.target.value)} 
            />
          </div>
          <div>
            <label className="block text-gray-600">Valor Pago</label>
            <input 
              type="number" 
              step="0.01"
              className="w-full p-2 border rounded" 
              value={repassePago} 
              onChange={(e) => setRepassePago(e.target.value)} 
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-gray-600">Valor da Comissão</label>
            <input 
              type="number" 
              step="0.01"
              className="w-full p-2 border rounded" 
              value={valorComissao} 
              onChange={(e) => setValorComissao(e.target.value)} 
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-gray-600">Percentual do Repasse (%)</label>
            <input 
              type="number" 
              step="0.01"
              min="0"
              max="100"
              className="w-full p-2 border rounded" 
              value={percentualRepasse} 
              onChange={(e) => setPercentualRepasse(e.target.value)} 
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-gray-600">Observações</label>
            <textarea
              className="w-full p-2 border rounded"
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={3}
              placeholder="Informações adicionais sobre este repasse..."
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button 
              type="submit" 
              disabled={submitting} 
              className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {submitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RepasseFormModal;
